import React, { createContext, FC, ReactNode, useContext, useEffect } from 'react';
import flagsmith from 'flagsmith';
import { FlagsmithProvider, useFlags } from 'flagsmith/react';
import { IFlagsmithFeature } from 'flagsmith/types';
import { FEATURE_FLAG_POLL_INTERVAL_BASELINE_MS, FEATURE_FLAG_POLL_INTERVAL_MAINTENANCE_MS } from 'config/const';
import { FeatureFlags } from 'types';

const FLAGSMITH_API_KEY = process.env.REACT_APP_FLAGSMITH_API_KEY;

const ALL_FLAG_NAMES = Object.values(FeatureFlags);

type IFeatureFlagsMap = Partial<Record<FeatureFlags, IFlagsmithFeature>>;

interface IProps {
  children: ReactNode;
}

interface IContentProps {
  children: ReactNode;
}

/** Exported so tests can supply flags directly instead of standing up Flagsmith. */
export const FeatureFlagsContext = createContext<IFeatureFlagsMap>({});

export const useFeatureFlags = (): IFeatureFlagsMap => useContext(FeatureFlagsContext);

const CustomFeatureFlagsProvider: FC<IContentProps> = ({ children }) => {
  const allFlags = useFlags(ALL_FLAG_NAMES);
  const maintenanceModeEnabled = allFlags[FeatureFlags.MAINTENANCE_MODE]?.enabled ?? false;

  useEffect(() => {
    const interval = maintenanceModeEnabled ? FEATURE_FLAG_POLL_INTERVAL_MAINTENANCE_MS : FEATURE_FLAG_POLL_INTERVAL_BASELINE_MS;
    flagsmith.startListening(interval);
    return () => flagsmith.stopListening();
  }, [maintenanceModeEnabled]);

  return <FeatureFlagsContext.Provider value={allFlags}>{children}</FeatureFlagsContext.Provider>;
};

export const FeatureFlagsProvider: FC<IProps> = ({ children }) => (
  <FlagsmithProvider flagsmith={flagsmith} options={{ environmentID: FLAGSMITH_API_KEY!, cacheFlags: true }}>
    <CustomFeatureFlagsProvider>{children}</CustomFeatureFlagsProvider>
  </FlagsmithProvider>
);
